import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  SupplierOrganizationCreatePayload,
  SupplierOrganizationDto,
  SupplierOrganizationUpdatePayload,
} from '@/features/suppliers/lib/supplier-organization-types'
import { organizationSupplierInstallationsQueryKey } from '@/features/suppliers/hooks/use-supplier-installations'
import { apiClient } from '@/shared/api/apiClient'
import { useSession } from '@/shared/hooks/useSession'

export type {
  SupplierOrganizationCreatePayload,
  SupplierOrganizationDto,
  SupplierOrganizationUpdatePayload,
}

const supplierOrganizationsQueryKey = (organizationId: string | undefined) =>
  ['supplier-organizations', organizationId] as const

/** Lists supplier organizations for the current session organization. */
export const useSupplierOrganizationsQuery = () => {
  const {
    session: {
      organization: { id: organizationId },
    },
  } = useSession()

  return useQuery({
    queryKey: supplierOrganizationsQueryKey(organizationId),
    queryFn: async () => {
      const response = await apiClient.get<SupplierOrganizationDto[]>(
        `/v2/organizations/${organizationId}/supplier-organizations`
      )
      return response.data
    },
    enabled: Boolean(organizationId),
  })
}

export const supplierOrganizationQueryKey = (
  organizationId: string | undefined,
  supplierOrganizationId: string | undefined
) => ['supplier-organization', organizationId, supplierOrganizationId] as const

/** Fetches a single supplier organization by id for the given or session organization. */
export const useSupplierOrganizationQuery = (
  organizationId: string | undefined,
  supplierOrganizationId: string | undefined
) => {
  const {
    session: {
      organization: { id: sessionOrganizationId },
    },
  } = useSession()

  const orgId = organizationId ?? sessionOrganizationId

  return useQuery({
    queryKey: supplierOrganizationQueryKey(orgId, supplierOrganizationId),
    queryFn: async () => {
      const response = await apiClient.get<SupplierOrganizationDto>(
        `/v2/organizations/${orgId}/supplier-organizations/${supplierOrganizationId}`
      )
      return response.data
    },
    enabled: Boolean(orgId && Boolean(supplierOrganizationId)),
  })
}

/** Creates, updates, and deletes supplier organizations for the current session organization. */
export const useSupplierOrganizationMutations = () => {
  const queryClient = useQueryClient()
  const {
    session: {
      organization: { id: organizationId },
    },
  } = useSession()

  const invalidateList = () =>
    queryClient.invalidateQueries({
      queryKey: supplierOrganizationsQueryKey(organizationId),
    })

  const addSupplierOrganization = useMutation({
    mutationFn: async (payload: SupplierOrganizationCreatePayload) => {
      const response = await apiClient.post<SupplierOrganizationDto>(
        `/v2/organizations/${organizationId}/supplier-organizations`,
        payload
      )
      return response.data
    },
    onSuccess: invalidateList,
  })

  const updateSupplierOrganization = useMutation({
    mutationFn: async ({
      supplierOrganizationId,
      payload,
    }: {
      supplierOrganizationId: string
      payload: SupplierOrganizationUpdatePayload
    }) => {
      const response = await apiClient.put<SupplierOrganizationDto>(
        `/v2/organizations/${organizationId}/supplier-organizations/${supplierOrganizationId}`,
        payload
      )
      return response.data
    },
    onSuccess: async (_data, variables) => {
      await invalidateList()
      await queryClient.invalidateQueries({
        queryKey: supplierOrganizationQueryKey(
          organizationId,
          variables.supplierOrganizationId
        ),
      })
    },
  })

  const deleteSupplierOrganization = useMutation({
    mutationFn: async (supplierOrganizationId: string) => {
      await apiClient.delete(
        `/v2/organizations/${organizationId}/supplier-organizations/${supplierOrganizationId}`
      )
    },
    onSuccess: async () => {
      await invalidateList()
      await queryClient.invalidateQueries({
        queryKey: organizationSupplierInstallationsQueryKey(organizationId),
      })
    },
  })

  return {
    addSupplierOrganization,
    updateSupplierOrganization,
    deleteSupplierOrganization,
  }
}